import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getGuestId, setPendingPairing } from "@/lib/guest";
import { AccountGateDialog } from "@/components/AccountGateDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PairingRecipes } from "@/components/RecipeBlock";
import { ReelLoader } from "@/components/ReelLoader";
import { Search, Save, Globe, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";

const loaderLabels = [
  "Rolling the film...",
  "Consulting the chef...",
  "Shaking the cocktails...",
  "Plating your pairing...",
];

export const GeneratePanel = ({ showHeader = true }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuest = user === false;
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [isPublic, setIsPublic] = useState(false);
  const [loaderLabel, setLoaderLabel] = useState(loaderLabels[0]);
  const [gate, setGate] = useState({ open: false, title: undefined, description: undefined });

  const generate = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    setResult(null);
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % loaderLabels.length;
      setLoaderLabel(loaderLabels[i]);
    }, 2200);
    try {
      const payload = { movie_title: title.trim() };
      if (isGuest) payload.guest_id = getGuestId();
      const { data } = await api.post("/generate", payload);
      setResult(data);
      setIsPublic(false);
      if (isGuest) setPendingPairing(data);
    } catch (err) {
      if (err.response?.status === 429) {
        setGate({
          open: true,
          title: "That's a wrap on free pairings",
          description:
            "Looks like you've used your free pairings. Create a free account to keep going, it only takes a moment.",
        });
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed");
      }
    } finally {
      clearInterval(interval);
      setLoading(false);
    }
  };

  const save = async () => {
    if (isGuest) {
      setGate({
        open: true,
        title: "Save this pairing",
        description:
          "Create a free account to save your pairings, rate them, and build your collection.",
      });
      return;
    }
    setSaving(true);
    try {
      await api.post("/pairings", { ...result, is_public: isPublic });
      toast.success("Pairing saved to your collection!");
      navigate("/pairings");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="generate-panel">
      {showHeader && (
        <div className="mb-8 animate-fade-up">
          <span className="uppercase text-xs tracking-[0.3em] text-[#E50914]">Generate</span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2">
            What are you watching tonight?
          </h1>
          <p className="text-white/50 mt-3">
            Enter a movie title and we'll compose the perfect meal to match.
          </p>
        </div>
      )}

      <form onSubmit={generate} className="flex flex-col sm:flex-row gap-3 mb-10">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="generate-title-input"
            placeholder="e.g. Pulp Fiction, Ratatouille, Blade Runner..."
            className="pl-12 h-14 bg-[#0d0d0d] border-white/10 rounded-full text-base"
            disabled={loading}
          />
        </div>
        <Button
          type="submit"
          disabled={loading || !title.trim()}
          data-testid="generate-submit-btn"
          className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-14 px-8 text-base"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Generate
        </Button>
      </form>

      {loading && <ReelLoader label={loaderLabel} />}

      {result && !loading && (
        <div className="animate-fade-up" data-testid="generate-result">
          <div className="rounded-2xl border border-[#E50914]/30 bg-[#E50914]/5 p-6 mb-5">
            <p className="uppercase text-xs tracking-[0.2em] text-[#E50914] mb-2">
              {result.movie_title}
            </p>
            <p className="text-lg font-light leading-relaxed text-white/85">
              {result.theme_description}
            </p>
          </div>

          <PairingRecipes pairing={result} />

          <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            {isGuest ? (
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-white/40" />
                <div>
                  <p className="text-white text-sm font-medium">Save this pairing</p>
                  <p className="text-xs text-white/40">
                    Create a free account to save, rate, and share your pairings.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {isPublic ? (
                  <Globe className="w-5 h-5 text-[#5EC5A0]" />
                ) : (
                  <Lock className="w-5 h-5 text-white/40" />
                )}
                <div>
                  <Label htmlFor="public-toggle" className="text-white cursor-pointer">
                    Make public
                  </Label>
                  <p className="text-xs text-white/40">
                    {isPublic
                      ? "Everyone can see this in the Community feed."
                      : "Only you can see this pairing."}
                  </p>
                </div>
                <Switch
                  id="public-toggle"
                  checked={isPublic}
                  onCheckedChange={setIsPublic}
                  data-testid="generate-public-toggle"
                />
              </div>
            )}
            <Button
              onClick={save}
              disabled={saving}
              data-testid="generate-save-btn"
              className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-11 px-8"
            >
              <Save className="w-4 h-4 mr-2" />
              {isGuest ? "Save Pairing" : saving ? "Saving..." : "Save Pairing"}
            </Button>
          </div>
        </div>
      )}

      <AccountGateDialog
        open={gate.open}
        onOpenChange={(o) => setGate((g) => ({ ...g, open: o }))}
        title={gate.title}
        description={gate.description}
      />
    </div>
  );
};
